import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../api/client'
import type { ChatCommandPreviewResponse, ChatCommandSuggestion } from '../../api/client'

/** Debounced preview with one request at a time and only the latest settled text queued. */
export function useCommandPreview(conversationId: string, text: string): ChatCommandPreviewResponse | null {
  const trimmed = text.trim()
  const key = `${conversationId}\0${trimmed}`
  const generation = useRef(0)
  const inFlight = useRef(false)
  const pending = useRef<{ conversationId: string; text: string; generation: number } | null>(null)
  const [entry, setEntry] = useState<{ key: string; value: ChatCommandPreviewResponse | null } | null>(null)
  const run = useCallback(async function runRequest(id: string, value: string, gen: number): Promise<void> {
    inFlight.current = true
    let result: ChatCommandPreviewResponse | null = null
    try {
      if (typeof api.previewChatCommand === 'function') result = await api.previewChatCommand(id, value)
    } catch { /* unavailable previews are intentionally silent */ }
    if (generation.current === gen) setEntry({ key: `${id}\0${value}`, value: result })
    inFlight.current = false
    const next = pending.current
    pending.current = null
    if (next) void runRequest(next.conversationId, next.text, next.generation)
  }, [])

  useEffect(() => {
    const gen = ++generation.current
    setEntry(null)
    pending.current = null
    if (!trimmed) return
    let active = true
    const timer = setTimeout(() => {
      if (!active) return
      if (inFlight.current) {
        pending.current = { conversationId, text: trimmed, generation: gen }
        return
      }
      void run(conversationId, trimmed, gen)
    }, 200)
    return () => { active = false; clearTimeout(timer) }
  }, [conversationId, key, trimmed, run])

  return entry?.key === key ? entry.value : null
}

export function CommandPreviewChip({ preview }: { preview: NonNullable<ChatCommandPreviewResponse['preview']> }) {
  const command = preview.state === 'command'
  const argument = preview.argument
  return <div data-testid="chat-command-preview" data-state={preview.state}
    className={`rounded-md px-3 py-2 text-sm ${command ? 'bg-indigo-50 text-indigo-800' : 'bg-amber-50 text-amber-900'}`}>
    {command
      ? <>▶ Will run: {preview.activity}{argument ? <> · {argument.input}: {argument.value}{argument.defaulted ? ' (default)' : ''}</> : null}</>
      : <>Starts {preview.activity}, but this is not one of its commands: {preview.usage}</>}
  </div>
}

export function CommandSuggestionList({
  suggestions, highlighted, onPick,
}: {
  suggestions: NonNullable<ChatCommandPreviewResponse['suggestions']>
  highlighted: number | null
  onPick: (text: string) => void
}) {
  return <div role="listbox" data-testid="chat-command-suggestions" className="rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
    <div className="px-2 pb-1 text-xs font-semibold text-gray-500">{suggestions.input.toUpperCase()}</div>
    {suggestions.items.map((item: ChatCommandSuggestion, index) => <div key={item.value} role="option"
      aria-selected={highlighted === index} onMouseDown={event => { event.preventDefault(); onPick(item.text) }}
      className={`cursor-pointer rounded px-2 py-1 text-sm ${highlighted === index ? 'bg-indigo-100' : 'hover:bg-gray-100'}`}>
      {item.value}
    </div>)}
  </div>
}
